const shopLocale = document.body.dataset.locale;
const alertMessage = (limit) => {
  if (shopLocale === "en") return `Limit: ${limit} per customer.`;
  return `この商品はお一人様 ${limit} 個までの購入制限があります。`;
};
const eventConflictMessage = () => {
  if (shopLocale === "en")
    return "Your cart contains items from a different event. Please empty your cart before adding this item.";
  return "カート内に別の大会の商品が入っています。先にカートを空にしてから購入してください。";
};

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;

        this.submitButton = this.querySelector('[type="submit"]');
        this.spinner = this.querySelector(".loading__spinner");
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        if (document.querySelector("cart-drawer")) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        // ✅ Buy It Nowボタン制御
        this.paymentButton = this.querySelector(
          ".shopify-payment-button__button"
        );
        if (this.paymentButton) {
          this.paymentButton.addEventListener(
            "click",
            this.onBuyNowHandler.bind(this)
          );
        }

        // ✅ クリック連打防止
        this._submittingLock = false;

        // ✅ 動的チェックアウト制御（Buy It Now iframe含む）
        const dynamicCheckoutButton = this.form.querySelector(
          ".shopify-payment-button"
        );
        if (dynamicCheckoutButton) {
          dynamicCheckoutButton.addEventListener("click", async (e) => {
            e.stopImmediatePropagation();
            e.preventDefault();
            dynamicCheckoutButton.style.pointerEvents = "none";

            const productId = this.dataset.productId;
            const desiredQty = this.getDesiredQty();
            const { limit, purchased } = this.getLimitAndPurchased();
            const newEvent = (this.dataset.event || "").trim();

            const { quantity: cartQty, items: cartItems } =
              await this.getCartInfo(productId);

            // ① 大会チェック
            if (this.checkEventConflict(newEvent, cartItems)) {
              alert(eventConflictMessage());
              setTimeout(() => {
                dynamicCheckoutButton.style.pointerEvents = "";
              }, 300);
              return false;
            }

            // ② order_limit チェック
            const total = desiredQty + cartQty + purchased;
            if (limit && total > limit) {
              const rest = Math.max(0, limit - (cartQty + purchased));
              if (rest <= 0) {
                alert(alertMessage(limit));
              } else {
                alert(
                  `${alertMessage(limit)}\nあと ${rest} 個まで購入可能です。`
                );
              }
              setTimeout(() => {
                dynamicCheckoutButton.style.pointerEvents = "";
              }, 300);
              return false;
            }

            const realButton = dynamicCheckoutButton.querySelector("button");
            if (realButton) realButton.click();

            setTimeout(() => {
              dynamicCheckoutButton.style.pointerEvents = "";
            }, 300);
          });
        }
      }

      /* =========================
       * 共通：制限＆購入済み数を取得
       * ========================= */
      getLimitAndPurchased() {
        const limit =
          this.parseIntSafe(this.dataset.limit, 0) ||
          this.parseIntSafe(this.dataset.originalLimit, 0) ||
          (typeof window !== "undefined"
            ? Number(window.productOrderLimit || 0)
            : 0);

        const purchased =
          this.parseIntSafe(this.dataset.purchased, 0) ||
          this.parseIntSafe(this.dataset.originalPurchased, 0) ||
          (typeof window !== "undefined"
            ? Number(window.productPurchasedQty || 0)
            : 0);

        return { limit, purchased };
      }

      /* =========================
       * メイン送信処理
       * ========================= */
      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this._submittingLock) return;
        this._submittingLock = true;

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.setSubmitting(true);

        try {
          const productId = this.dataset.productId;
          const desiredQty = this.getDesiredQty();
          const { limit, purchased } = this.getLimitAndPurchased();
          const newEvent = (this.dataset.event || "").trim();

          // カートを1回 fetch（大会チェック・個数チェック両方に使用）
          const { quantity: cartQty, items: cartItems } =
            await this.getCartInfo(productId);

          // ✅ ① 大会チェック（order_limit より先に実行）
          if (this.checkEventConflict(newEvent, cartItems)) {
            this.handleErrorMessage(eventConflictMessage());
            return;
          }

          // ✅ ② 購入制限チェック
          if (limit && limit > 0) {
            const afterQty = purchased + cartQty + desiredQty;
            if (afterQty > limit) {
              const rest = Math.max(0, limit - (purchased + cartQty));
              if (rest <= 0) {
                this.handleErrorMessage(alertMessage(limit));
                return;
              }
              if (desiredQty > rest) {
                this.handleErrorMessage(
                  `${alertMessage(limit)}\nあと ${rest} 個まで購入可能です。`
                );
                return;
              }
            }
          }

          // ✅ カートに追加
          const data = await this.performAddToCart();
          return this.afterAddToCart(data);
        } catch (e) {
          console.error("ProductForm error:", e);
          this.handleErrorMessage("予期せぬエラーが発生しました。");
        } finally {
          this.setSubmitting(false);
          setTimeout(() => (this._submittingLock = false), 1000);
        }
      }

      /* =========================
       * 今すぐ購入制御（Buy It Now）
       * ========================= */
      async onBuyNowHandler(e) {
        if (this._submittingLock) return;
        this._submittingLock = true;
        setTimeout(() => (this._submittingLock = false), 1000);

        const productId = this.dataset.productId;
        const { limit, purchased } = this.getLimitAndPurchased();
        const newEvent = (this.dataset.event || "").trim();

        try {
          const { quantity: cartQty, items: cartItems } =
            await this.getCartInfo(productId);

          // ① 大会チェック
          if (this.checkEventConflict(newEvent, cartItems)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            alert(eventConflictMessage());
            return false;
          }

          // ② order_limit チェック
          const afterQty = purchased + cartQty + 1;
          if (limit > 0 && afterQty > limit) {
            e.preventDefault();
            e.stopImmediatePropagation();

            const rest = Math.max(0, limit - (cartQty + purchased));
            if (rest <= 0) {
              alert(alertMessage(limit));
            } else {
              alert(
                `${alertMessage(limit)}\nあと ${rest} 個まで購入可能です。`
              );
            }
            return false;
          }
        } catch (err) {
          console.error("Buy Now check error:", err);
        }
      }

      /* =========================
       * カート追加後の更新処理
       * ========================= */
      afterAddToCart(data) {
        if (!data || !data.sections) {
          console.warn("cartData incomplete, fetching /cart?sections=...");

          const sections = this.cart?.getSectionsToRender
            ? this.cart.getSectionsToRender().map((s) => s.section)
            : ["cart-notification-product", "cart-icon-bubble"];

          const url = `/cart?sections=${sections.join(",")}`;

          fetch(url)
            .then((r) => r.json())
            .then((cartSections) => {
              if (this.cart?.renderContents) {
                this.cart.renderContents({ sections: cartSections });
              }
              this.publishCartUpdate({ sections: cartSections });
            })
            .catch((e) => console.error("cart fetch with sections failed", e));

          return;
        }

        if (this.cart?.renderContents) this.cart.renderContents(data);
        this.publishCartUpdate(data);
      }

      publishCartUpdate(cartData) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: this.form.querySelector("[name=id]").value,
          cartData: cartData,
        });
      }

      /* =========================
       * /cart/add 実行
       * ========================= */
      async performAddToCart() {
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((s) => s.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // 大会名をカートアイテムのプロパティとして保存（次回追加時の比較に使用）
        const eventName = (this.dataset.event || "").trim();
        if (eventName) {
          formData.append("properties[_event_name]", eventName);
        }

        config.body = formData;

        const resp = await fetch(`${routes.cart_add_url}`, config);
        const data = await resp.json();

        if (data?.status) {
          this.handleErrorMessage(
            data.description ||
              data.message ||
              "カートに追加できませんでした。"
          );
          this.error = true;
          throw new Error("Add to cart failed");
        }

        this.error = false;
        return data;
      }

      /* =========================
       * カート情報取得（商品数量 + 全アイテム）
       * 大会チェックと個数チェックを1回のfetchで対応
       * ========================= */
      async getCartInfo(productId) {
        try {
          const res = await fetch("/cart.js", { credentials: "same-origin" });
          const cart = await res.json();
          const items = cart?.items || [];
          const quantity = items.reduce(
            (sum, item) =>
              item.product_id == productId ? sum + item.quantity : sum,
            0
          );
          return { quantity, items };
        } catch (e) {
          console.warn("cart.js の取得に失敗しました。", e);
          return { quantity: 0, items: [] };
        }
      }

      /* =========================
       * 大会競合チェック
       * 新商品またはカート全商品が未設定なら許可
       * いずれかのカートアイテムと大会名が異なればブロック
       * ========================= */
      checkEventConflict(newEvent, cartItems) {
        if (!newEvent) return false;
        for (const item of cartItems) {
          const cartEvent = (item.properties?._event_name || "").trim();
          if (cartEvent && cartEvent !== newEvent) return true;
        }
        return false;
      }

      /* =========================
       * 数量取得（UIカウンター対応）
       * ========================= */
      getDesiredQty() {
        let input =
          this.form.querySelector('input[name="quantity"]') ||
          this.form.querySelector('input.quantity__input');

        if (!input) {
          input =
            document.querySelector(
              'quantity-input .quantity__input[name="quantity"]'
            ) ||
            document.querySelector('input.quantity__input[name="quantity"]') ||
            document.querySelector('input[name="quantity"]');
        }

        if (!input) {
          console.warn("❗数量入力が見つかりません（document全体でも）");
          return 1;
        }

        const val = parseInt(input.value, 10);
        return Number.isFinite(val) && val > 0 ? val : 1;
      }

      /* =========================
       * ヘルパー類
       * ========================= */
      parseIntSafe(value, fallback) {
        const n = parseInt(value, 10);
        return Number.isFinite(n) ? n : fallback;
      }

      setSubmitting(isSubmitting) {
        this.submitButton.setAttribute(
          "aria-disabled",
          isSubmitting ? "true" : "false"
        );
        this.submitButton.classList.toggle("loading", isSubmitting);
        if (this.spinner)
          this.spinner.classList.toggle("hidden", !isSubmitting);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, resetText = true) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
        } else {
          this.submitButton.removeAttribute("disabled");
        }

        if (!resetText) return;

        const soldOutMessage = this.submitButton.querySelector(
          ".sold-out-message"
        );
        const defaultText = this.submitButton.querySelector(
          "span:not(.sold-out-message)"
        );

        if (soldOutMessage && !soldOutMessage.classList.contains("hidden")) {
          soldOutMessage.classList.add("hidden");
        }
        if (defaultText && defaultText.classList.contains("hidden")) {
          defaultText.classList.remove("hidden");
        }
      }
    }
  );
}
